import { useLocation } from 'react-router-dom';

import type { Lang } from '@/types';
import { fr } from './fr';
import { en } from './en';
import type { UIStrings } from './fr';

export type { UIStrings } from './fr';

/** Prefixe de route de la version anglaise. La francaise n'en a pas. */
export const EN_PREFIX = '/en';

/**
 * Langue d'un chemin. C'est l'URL qui decide, jamais un etat applicatif :
 * react-snap pre-rend chaque route hors navigateur reel, donc toute langue
 * memorisee ailleurs produirait un HTML statique dans la mauvaise langue.
 */
export function langFromPath(pathname: string): Lang {
  return pathname === EN_PREFIX || pathname.startsWith(`${EN_PREFIX}/`) ? 'en' : 'fr';
}

/** Langue de la route courante. */
export function useLang(): Lang {
  return langFromPath(useLocation().pathname);
}

/**
 * Dictionnaire d'une langue donnee.
 *
 * Un ternaire plutot qu'un Record indexe : `noUncheckedIndexedAccess` ajoute
 * `| undefined` a toute lecture par cle, qu'il faudrait ensuite ecarter par un
 * cast. Ici le compilateur voit deux valeurs certaines.
 */
export function dictionary(lang: Lang): UIStrings {
  return lang === 'en' ? en : fr;
}

/** Dictionnaire de libelles de la route courante. */
export function useT(): UIStrings {
  return dictionary(useLang());
}

/** Chemin sans son prefixe de langue : `/en/asterion` -> `/asterion`. */
export function stripLang(pathname: string): string {
  if (langFromPath(pathname) === 'fr') return pathname;
  const rest = pathname.slice(EN_PREFIX.length);
  return rest === '' ? '/' : rest;
}

/** Chemin de `route` dans `lang`. `/asterion` en anglais -> `/en/asterion`. */
export function pathFor(route: string, lang: Lang): string {
  const clean = route.startsWith('/') ? route : `/${route}`;
  if (lang === 'fr') return clean;
  return clean === '/' ? EN_PREFIX : `${EN_PREFIX}${clean}`;
}

/**
 * Equivalent du chemin courant dans l'autre langue.
 *
 * Les articles n'existent qu'en francais : depuis `/articles/<slug>`, la version
 * anglaise la plus proche est l'index `/en/articles`, dont les cartes menent aux
 * traductions publiees sur le blog Asterion.
 */
export function altPath(pathname: string, target: Lang): string {
  const route = stripLang(pathname);
  if (target === 'en' && route.startsWith('/articles/')) return `${EN_PREFIX}/articles`;
  return pathFor(route, target);
}

/** Etiquette du bouton de bascule : on affiche la langue vers laquelle on va. */
export function otherLang(lang: Lang): Lang {
  return lang === 'fr' ? 'en' : 'fr';
}
